// Copyright 2025-2026 Florian Gallon. All Rights reserved.
import React from 'react';
import { Loader2 } from 'lucide-react';

const Button = ({
  children,
  variant = 'primary',
  size = 'medium',
  disabled = false,
  loading = false,
  iconLeft,
  iconRight,
  fullWidth = false,
  className = '',
  onClick,
  type = 'button',
  ...props
}) => {
  const IconLeft = iconLeft;
  const IconRight = iconRight;

  // Base button classes with editorial styling
  // min-w-0/max-w-full: in tight flex rows (tablet cards) the label wraps inside the button instead of overflowing its neighbours
  const baseClasses = "inline-flex items-center justify-center gap-2 min-w-0 max-w-full text-center rounded-full font-ui font-semibold uppercase tracking-[0.03em] transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)] focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed active:translate-y-[1px]";

  // Size variants
  const sizeClasses = {
    sm: "px-3 py-1.5 text-xs",
    small: "px-4 py-2 text-xs",
    md: "px-5 py-2.5 text-sm",
    medium: "px-5 py-2.5 text-sm",
    lg: "px-6 py-3 text-base",
    large: "px-6 py-3 text-base"
  };

  // Variant classes — token-based only, no btn-editorial-* dependency
  // btn-v-* class names are hooks for CSS :hover rules in tokens.css
  const variantClasses = {
    primary:     'btn-v-primary bg-[var(--btn-primary-bg)] text-[var(--btn-primary-text)] border border-[var(--btn-primary-border)] shadow-[var(--btn-primary-shadow)]',
    secondary:   'btn-v-secondary bg-[var(--btn-secondary-bg)] text-[var(--btn-secondary-text)] border border-[var(--btn-secondary-border)]',
    ghost:       'btn-v-ghost bg-[var(--btn-ghost-bg)] text-[var(--btn-ghost-text)] border border-[var(--btn-ghost-border)]',
    danger:      'btn-v-danger bg-[var(--btn-danger-bg)] text-[var(--btn-danger-text)] border border-[var(--btn-danger-border)]',
    dangerGhost: 'btn-v-danger-ghost bg-transparent text-[var(--status-danger)] border border-[var(--status-danger)]',
  };

  const widthClass = fullWidth ? 'w-full' : '';
  const classes = `${baseClasses} ${sizeClasses[size]} ${variantClasses[variant]} ${widthClass} ${className}`;

  // Determine icon size based on button size
  const iconSize = size === 'small' || size === 'sm' ? 14 : size === 'large' || size === 'lg' ? 20 : 16;

  return (
    <button
      type={type}
      className={classes}
      disabled={disabled || loading}
      onClick={onClick}
      {...props}
    >
      {loading && <Loader2 size={iconSize} className="animate-spin" />}
      {!loading && IconLeft && <IconLeft size={iconSize} />}
      {children}
      {!loading && IconRight && <IconRight size={iconSize} />}
    </button>
  );
};

export default Button;
